import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Calendar, ChevronLeft, Clock, Image as ImageIcon, MoreHorizontal, Pencil, Plus, Save, Stethoscope, Trash2 } from 'lucide-react';
import { staffApi } from '@/services/clinicApi';
import { normalizeImageValue } from '@/services/api';

const WEEKDAY_TO_DAY = {
  0: 'monday',
  1: 'tuesday',
  2: 'wednesday',
  3: 'thursday',
  4: 'friday',
  5: 'saturday',
  6: 'sunday',
};

const SERVICE_SCHEDULE_WEEKDAY_TO_DAY = {
  0: 'monday',
  1: 'tuesday',
  2: 'wednesday',
  3: 'thursday',
  4: 'friday',
  5: 'saturday',
  6: 'sunday',
};

const DAY_TO_SERVICE_SCHEDULE_WEEKDAY = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DAY_TO_INDEX = DAY_ORDER.reduce((accumulator, day, index) => {
  accumulator[day] = index;
  return accumulator;
}, {});

const TIME_SLOTS = [
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
  '18:00',
  '18:30',
  '19:00',
  '19:30',
  '20:00',
  '20:30',
  '21:00',
];

const EMPTY_WEEKLY_SCHEDULE = {
  saturday: { enabled: false, slots: [] },
  sunday: { enabled: false, slots: [] },
  monday: { enabled: false, slots: [] },
  tuesday: { enabled: false, slots: [] },
  wednesday: { enabled: false, slots: [] },
  thursday: { enabled: false, slots: [] },
  friday: { enabled: false, slots: [] },
};

const EMPTY_SLOT_FORM = {
  id: null,
  day: 'sunday',
  start: '09:00',
  end: '17:00',
  availability_type: 'in_clinic',
};

const EMPTY_SERVICE_SCHEDULE_FORM = {
  serviceLocalId: null,
  scheduleIndex: null,
  day: 'sunday',
  start: '09:00',
  end: '17:00',
};

const EMPTY_NEW_SERVICE_FORM = {
  name: '',
  description: '',
  parent_id: null,
  service_kind: 'category',
  duration_minutes: '30',
  price: '0',
  is_active: true,
  coming_soon: false,
  allow_multi_booking: false,
  affects_general_appointments: false,
  affected_service_ids: [],
  schedules: [],
  image: null,
  imagePreview: null,
  remove_image: false,
};

const padTimePart = (value) => String(value).padStart(2, '0');
const REFERENCE_SUNDAY_UTC = Date.UTC(2020, 0, 5, 0, 0, 0, 0);

const normalizeTimeValue = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const timeMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (timeMatch) {
    return `${padTimePart(timeMatch[1])}:${timeMatch[2]}`;
  }

  const normalizedDateTime = trimmedValue.includes(' ') ? trimmedValue.replace(' ', 'T') : trimmedValue;
  const parsedDate = new Date(normalizedDateTime);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return `${padTimePart(parsedDate.getHours())}:${padTimePart(parsedDate.getMinutes())}`;
};

const toApiTimeValue = (value, fallback) => normalizeTimeValue(value) || fallback;

const getValidTimeZone = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date());
    return value;
  } catch (error) {
    return null;
  }
};

const persistDoctorTimezoneOverride = (timeZone) => {
  const validTimeZone = getValidTimeZone(timeZone);

  try {
    if (validTimeZone) {
      sessionStorage.setItem('doctor_timezone_override', validTimeZone);
      localStorage.setItem('doctor_timezone_override', validTimeZone);
    } else {
      sessionStorage.removeItem('doctor_timezone_override');
      localStorage.removeItem('doctor_timezone_override');
    }
  } catch (error) {
    // Ignore storage availability issues.
  }
};

const extractDoctorTimezone = (source) => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const directKeys = ['timezone', 'time_zone', 'tz', 'doctor_timezone', 'doctor_time_zone', 'partner_timezone', 'partner_tz'];
  for (const key of directKeys) {
    const resolved = getValidTimeZone(source[key]);
    if (resolved) {
      return resolved;
    }
  }

  const nestedKeys = ['doctor', 'partner', 'user', 'profile', 'clinic'];
  for (const key of nestedKeys) {
    const resolved = extractDoctorTimezone(source[key]);
    if (resolved) {
      return resolved;
    }
  }

  return null;
};

const getZonedDateParts = (date, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const weekdayToken = parts.find((part) => part.type === 'weekday')?.value?.toLowerCase();
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
  const weekdayIndex = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
  }[weekdayToken];

  return {
    weekdayIndex,
    hour,
    minute,
  };
};

const buildUtcDateForDayAndTime = (dayKey, timeValue) => {
  const normalizedTime = normalizeTimeValue(timeValue);
  const dayIndex = DAY_TO_INDEX[dayKey];
  if (normalizedTime == null || dayIndex == null) {
    return null;
  }

  const [hourText, minuteText] = normalizedTime.split(':');
  return new Date(Date.UTC(2020, 0, 5 + dayIndex, Number(hourText), Number(minuteText), 0, 0));
};

const convertApiScheduleToDoctorTime = (dayKey, timeValue, timeZone) => {
  const normalizedTime = normalizeTimeValue(timeValue);
  if (!normalizedTime) {
    return {
      day: dayKey,
      time: timeValue,
    };
  }

  const validTimeZone = getValidTimeZone(timeZone);
  if (!validTimeZone) {
    return {
      day: dayKey,
      time: normalizedTime,
    };
  }

  const directDate = new Date(typeof timeValue === 'string' && timeValue.includes('T') ? timeValue : '');
  const baseDate =
    !Number.isNaN(directDate.getTime()) && typeof timeValue === 'string' && timeValue.includes('T')
      ? directDate
      : buildUtcDateForDayAndTime(dayKey, normalizedTime);

  if (!baseDate) {
    return {
      day: dayKey,
      time: normalizedTime,
    };
  }

  const zonedParts = getZonedDateParts(baseDate, validTimeZone);
  return {
    day: DAY_ORDER[zonedParts.weekdayIndex] || dayKey,
    time: `${padTimePart(zonedParts.hour)}:${padTimePart(zonedParts.minute)}`,
  };
};

const convertDoctorTimeToApiSchedule = (dayKey, timeValue, timeZone) => {
  const normalizedTime = normalizeTimeValue(timeValue);
  if (!normalizedTime) {
    return {
      day: dayKey,
      time: timeValue,
    };
  }

  const validTimeZone = getValidTimeZone(timeZone);
  if (!validTimeZone) {
    return {
      day: dayKey,
      time: normalizedTime,
    };
  }

  const dayIndex = DAY_TO_INDEX[dayKey];
  if (dayIndex == null) {
    return {
      day: dayKey,
      time: normalizedTime,
    };
  }

  const [targetHour, targetMinute] = normalizedTime.split(':').map(Number);
  const naiveUtcTime = Date.UTC(2020, 0, 5 + dayIndex, targetHour, targetMinute, 0, 0);

  for (let deltaMinutes = -24 * 60; deltaMinutes <= 24 * 60; deltaMinutes += 1) {
    const candidate = new Date(naiveUtcTime - deltaMinutes * 60 * 1000);
    const zonedParts = getZonedDateParts(candidate, validTimeZone);

    if (
      zonedParts.weekdayIndex === dayIndex &&
      zonedParts.hour === targetHour &&
      zonedParts.minute === targetMinute
    ) {
      return {
        day: DAY_ORDER[candidate.getUTCDay()] || dayKey,
        time: `${padTimePart(candidate.getUTCHours())}:${padTimePart(candidate.getUTCMinutes())}`,
      };
    }
  }

  return {
    day: dayKey,
    time: normalizedTime,
  };
};

const normalizeServiceRelationId = (value) => {
  if (value == null) return null;
  if (Array.isArray(value)) return normalizeServiceRelationId(value[0]);
  if (typeof value === 'object') return normalizeServiceRelationId(value.id ?? value.value);

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : value;
};

const mapServiceRow = (service, index, fallbackDuration, doctorTimezone) => ({
  localId: `service-${service.id || index}`,
  id: service.id,
  parent_id: normalizeServiceRelationId(service.parent_id),
  service_kind: service.service_kind || (service.is_bookable === false ? 'category' : 'bookable'),
  name: service.name || '',
  description: service.description || '',
  duration_minutes: String(service.duration_minutes || fallbackDuration || 30),
  price: String(service.price || 0),
  is_active: service.is_active !== false,
  coming_soon: !!service.coming_soon,
  allow_multi_booking: !!(service.allow_multi_booking ?? service.allowMultiBooking),
  affects_general_appointments: !!(
    service.affects_general_appointments ?? service.affectsGeneralAppointments
  ),
  affected_service_ids: Array.isArray(service.affected_service_ids ?? service.affectedServiceIds)
    ? [...(service.affected_service_ids ?? service.affectedServiceIds)]
    : [],
  has_sub_service: !!service.has_sub_service || (Array.isArray(service.children) && service.children.length > 0),
  schedules: Array.isArray(service.schedules)
    ? service.schedules.map((schedule, scheduleIndex) => {
        const apiDay = schedule.day || SERVICE_SCHEDULE_WEEKDAY_TO_DAY[Number(schedule.day_of_week)] || 'sunday';
        const normalizedStart = convertApiScheduleToDoctorTime(
          apiDay,
          schedule.start_at || schedule.start_time,
          doctorTimezone
        );
        const normalizedEnd = convertApiScheduleToDoctorTime(
          apiDay,
          schedule.end_at || schedule.end_time,
          doctorTimezone
        );

        return {
          localId: schedule.id || `schedule-${service.id || index}-${scheduleIndex}`,
          day: normalizedStart.day,
          start: normalizedStart.time || '09:00',
          end: normalizedEnd.time || '17:00',
          is_online: !!schedule.is_online,
        };
      })
    : [],
  children: Array.isArray(service.children)
    ? service.children.map((child, childIndex) =>
        mapServiceRow(child, `${index}-${childIndex}`, fallbackDuration, doctorTimezone)
      )
    : [],
  image: normalizeImageValue(
    service.image ||
      service.image_url ||
      service.media?.[0]?.url ||
      service.media?.[0]?.thumb ||
      service.media?.[0]?.icon ||
      ''
  ) || null,
  has_media: !!(service.has_media || service.image || service.image_url || service.media?.length),
  image_file: null,
  remove_image: false,
});

const formatDisplayTime = (value, lang) => {
  const normalizedValue = normalizeTimeValue(value) || value;
  if (!normalizedValue || !normalizedValue.includes(':')) return normalizedValue;

  const [hourText, minuteText] = normalizedValue.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return normalizedValue;
  }

  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  const suffix = hour >= 12 ? (lang === 'ar' ? 'م' : 'PM') : lang === 'ar' ? 'ص' : 'AM';
  return `${normalizedHour}:${String(minute).padStart(2, '0')} ${suffix}`;
};

const TimeEdgeBadge = ({ label, value, tone = 'start' }) => {
  const toneClass =
    tone === 'start'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300'
      : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300';

  return (
    <div className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${toneClass}`}>
      <span className="text-sm font-bold">{label}</span>
      <span className="text-base font-black">{value}</span>
    </div>
  );
};

const getActiveChildServices = (service) =>
  Array.isArray(service.children) ? service.children.filter((child) => child.is_active !== false) : [];

const getServiceIdentityKey = (service) => {
  if (!service) return null;
  if (service.id != null) return `id-${service.id}`;
  return service.localId ? `local-${service.localId}` : null;
};

const flattenServicesTree = (services) => {
  const result = [];
  const seenKeys = new Set();

  const visit = (service) => {
    if (!service) return;
    const serviceKey = getServiceIdentityKey(service);
    if (serviceKey && seenKeys.has(serviceKey)) return;
    if (serviceKey) {
      seenKeys.add(serviceKey);
    }

    result.push(service);
    if (Array.isArray(service.children)) {
      service.children.forEach(visit);
    }
  };

  services.forEach(visit);
  return result;
};

const collectDescendantLocalIds = (service) => {
  const ids = new Set();

  const visit = (node) => {
    if (!node || !Array.isArray(node.children)) return;
    node.children.forEach((child) => {
      ids.add(child.localId);
      visit(child);
    });
  };

  visit(service);
  return ids;
};

const findServiceByLocalId = (services, localId) => {
  for (const service of services) {
    if (service.localId === localId) {
      return service;
    }

    const childMatch = Array.isArray(service.children)
      ? findServiceByLocalId(service.children, localId)
      : null;

    if (childMatch) {
      return childMatch;
    }
  }

  return null;
};

const updateServiceInTree = (services, localId, updater) =>
  services.map((service) => {
    if (service.localId === localId) {
      return typeof updater === 'function' ? updater(service) : { ...service, ...updater };
    }

    if (!Array.isArray(service.children) || service.children.length === 0) {
      return service;
    }

    return {
      ...service,
      children: updateServiceInTree(service.children, localId, updater),
    };
  });

const removeServiceFromTree = (services, localId) =>
  services
    .filter((service) => service.localId !== localId)
    .map((service) => ({
      ...service,
      children: Array.isArray(service.children)
        ? removeServiceFromTree(service.children, localId)
        : service.children,
    }));

const uniqueIds = (ids) => Array.from(new Set(ids.filter(Boolean)));

const removeDuplicateRootServices = (services) => {
  const nestedKeys = new Set();

  const collectNestedKeys = (service) => {
    if (!service || !Array.isArray(service.children)) return;

    service.children.forEach((child) => {
      const childKey = getServiceIdentityKey(child);
      if (childKey) {
        nestedKeys.add(childKey);
      }
      collectNestedKeys(child);
    });
  };

  services.forEach(collectNestedKeys);

  const seenRootKeys = new Set();

  return services
    .filter((service) => {
      const serviceKey = getServiceIdentityKey(service);
      if (!serviceKey) return true;
      if (nestedKeys.has(serviceKey) || seenRootKeys.has(serviceKey)) {
        return false;
      }
      seenRootKeys.add(serviceKey);
      return true;
    })
    .map((service) => ({
      ...service,
      children: Array.isArray(service.children)
        ? removeDuplicateRootServices(service.children)
        : service.children,
    }));
};

const buildServicesTree = (services) => {
  const flatServices = flattenServicesTree(services);
  const servicesById = new Map();

  const clonedServices = flatServices.map((service) => {
    const clonedService = {
      ...service,
      children: [],
    };

    if (clonedService.id != null) {
      servicesById.set(String(clonedService.id), clonedService);
    }

    return clonedService;
  });

  const roots = [];

  clonedServices.forEach((service) => {
    const parentId = normalizeServiceRelationId(service.parent_id);
    const parentService = parentId == null ? null : servicesById.get(String(parentId));

    if (parentService && parentService !== service) {
      parentService.children.push(service);
      return;
    }

    roots.push(service);
  });

  const markParents = (service) => {
    const children = Array.isArray(service.children)
      ? service.children.map(markParents)
      : [];

    return {
      ...service,
      children,
      has_sub_service: !!service.has_sub_service || children.length > 0,
    };
  };

  return removeDuplicateRootServices(roots.map(markParents));
};

const hasServiceSchedules = (service) =>
  Array.isArray(service?.schedules) && service.schedules.length > 0;

const isPrimaryService = (service) =>
  service?.is_active !== false &&
  !hasServiceSchedules(service) &&
  getActiveChildServices(service).length > 0;

const canBookServiceDirectly = (service) =>
  !service?.coming_soon &&
  !service?.has_sub_service &&
  Number(service?.duration_minutes || 0) > 0;

const isBookableService = (service) =>
  canBookServiceDirectly(service) && hasServiceSchedules(service);

const collectBookableServices = (services) => {
  const result = [];
  const seenKeys = new Set();

  const visit = (service) => {
    if (!service || service.is_active === false) {
      return;
    }

    const activeChildren = Array.isArray(service.children)
      ? service.children.filter((child) => child.is_active !== false)
      : [];

    if (activeChildren.length === 0) {
      if (isBookableService(service)) {
        const serviceKey = getServiceIdentityKey(service);
        if (!serviceKey || !seenKeys.has(serviceKey)) {
          if (serviceKey) {
            seenKeys.add(serviceKey);
          }
          result.push(service);
        }
      }
      return;
    }

    activeChildren.forEach(visit);
  };

  services.forEach(visit);
  return result;
};

const formatServicePrice = (value, lang) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    return lang === 'ar' ? 'بدون سعر' : 'No price';
  }

  return lang === 'ar' ? `${amount} ل.س` : `${amount} SYP`;
};

const SectionToggleButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold transition ${
      active
        ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/20'
        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
    }`}
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
  </button>
);

const ServiceOverviewCard = ({ service, lang, onEditService, onDeleteService }) => {
  const activeChildren = getActiveChildServices(service);
  const subtitle = [
    service.coming_soon ? (lang === 'ar' ? 'قريباً' : 'Coming soon') : lang === 'ar' ? 'نشط' : 'Active',
    activeChildren.length > 0
      ? lang === 'ar'
        ? `${activeChildren.length} خدمات فرعية`
        : `${activeChildren.length} sub services`
      : null,
    Number(service.duration_minutes) > 0
      ? lang === 'ar'
        ? `${service.duration_minutes} دقيقة`
        : `${service.duration_minutes} min`
      : null,
    service.schedules?.length
      ? lang === 'ar'
        ? `${service.schedules.length} جداول`
        : `${service.schedules.length} schedules`
      : null,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <div className="rounded-[24px] bg-white p-5 shadow-sm ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700">
      <div className="flex items-start gap-4">
        {service.image ? (
          <img src={service.image} alt={service.name || 'service'} className="h-16 w-16 rounded-2xl border object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
            <Stethoscope className="h-7 w-7" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">{service.name || (lang === 'ar' ? 'خدمة بدون اسم' : 'Untitled service')}</h3>
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
              {formatServicePrice(service.price, lang)}
            </span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                  <MoreHorizontal className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={onEditService}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {lang === 'ar' ? 'تعديل الخدمة' : 'Edit service'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDeleteService} className="text-red-600 focus:text-red-600">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {lang === 'ar' ? 'حذف الخدمة' : 'Delete service'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="mt-2 text-sm font-medium text-slate-500 dark:text-slate-300">{subtitle}</p>
          {service.description?.trim() ? (
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{service.description.trim()}</p>
          ) : null}
          {activeChildren.length ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {activeChildren.map((child) => (
                <span
                  key={child.localId}
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                >
                  {child.name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const BookableServiceCard = ({ service, lang }) => (
  <div className="rounded-[22px] bg-white p-4 shadow-sm ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700">
    <div className="flex items-start gap-4">
      {service.image ? (
        <img src={service.image} alt={service.name || 'service'} className="h-14 w-14 rounded-2xl border object-cover" />
      ) : (
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
          <Clock className="h-6 w-6" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-bold text-slate-900 dark:text-white">{service.name || (lang === 'ar' ? 'خدمة قابلة للحجز' : 'Bookable service')}</h3>
          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
            {formatServicePrice(service.price, lang)}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-500 dark:text-slate-300">
          {Number(service.duration_minutes) > 0 ? (
            <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
              {lang === 'ar' ? `${service.duration_minutes} دقيقة` : `${service.duration_minutes} min`}
            </span>
          ) : null}
          {service.schedules?.length ? (
            <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
              {lang === 'ar' ? `${service.schedules.length} جداول` : `${service.schedules.length} schedules`}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);

const ServiceManageCard = ({
  service,
  lang,
  onEditService,
  onDeleteService,
  onAddSchedule,
  onEditSchedule,
  onDeleteSchedule,
  resolveDayLabel,
}) => (
  <div className="rounded-[22px] bg-white p-4 shadow-sm ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700">
    <div className="flex items-start gap-4">
      {service.image ? (
        <img src={service.image} alt={service.name || 'service'} className="h-16 w-16 rounded-2xl border object-cover" />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
          <Stethoscope className="h-7 w-7" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {service.name || (lang === 'ar' ? 'خدمة بدون اسم' : 'Untitled service')}
            </h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              {service.description?.trim() || (lang === 'ar' ? 'لا يوجد وصف مضاف حالياً.' : 'No description added yet.')}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onEditService}>
                <Pencil className="mr-2 h-4 w-4" />
                {lang === 'ar' ? 'تعديل الخدمة' : 'Edit service'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDeleteService} className="text-red-600 focus:text-red-600">
                <Trash2 className="mr-2 h-4 w-4" />
                {lang === 'ar' ? 'حذف الخدمة' : 'Delete service'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-slate-500 dark:text-slate-300">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {formatServicePrice(service.price, lang)}
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {lang === 'ar' ? `${service.duration_minutes} دقيقة` : `${service.duration_minutes} min`}
          </span>
          <span className={`rounded-full px-3 py-1 ${service.is_active ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300'}`}>
            {service.is_active ? (lang === 'ar' ? 'مفعلة' : 'Active') : lang === 'ar' ? 'معطلة' : 'Inactive'}
          </span>
        </div>

        <div className="mt-4 rounded-[18px] bg-slate-50 p-4 dark:bg-slate-900/60">
          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onAddSchedule}
              className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
            >
              <Plus className="mr-1 h-4 w-4" />
              {lang === 'ar' ? 'إضافة جدول' : 'Add schedule'}
            </Button>
            <div className="text-right">
              <p className="text-sm font-extrabold text-slate-900 dark:text-white">{lang === 'ar' ? 'جداول الخدمة' : 'Service schedules'}</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                {service.schedules?.length
                  ? lang === 'ar'
                    ? `${service.schedules.length} جداول مضافة لهذه الخدمة`
                    : `${service.schedules.length} schedules added for this service`
                  : lang === 'ar'
                    ? 'لا توجد جداول مضافة بعد'
                    : 'No schedules added yet'}
              </p>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {service.schedules?.length ? (
              service.schedules.map((serviceSchedule, scheduleIndex) => (
                <div
                  key={serviceSchedule.localId || `${service.localId}-${scheduleIndex}`}
                  className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditSchedule(scheduleIndex)}
                        className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onDeleteSchedule(scheduleIndex)}
                        className="text-red-500 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="min-w-[260px] text-right">
                      <div className="text-sm font-bold text-slate-900 dark:text-white">{resolveDayLabel(serviceSchedule.day)}</div>
                      <div className="mt-2 space-y-2">
                        <TimeEdgeBadge label={lang === 'ar' ? 'من' : 'From'} value={formatDisplayTime(serviceSchedule.start, lang)} tone="start" />
                        <TimeEdgeBadge label={lang === 'ar' ? 'إلى' : 'To'} value={formatDisplayTime(serviceSchedule.end, lang)} tone="end" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-center text-sm font-medium text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {lang === 'ar' ? 'لا توجد جداول مضافة بعد' : 'No schedules added yet'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const BookableServiceManageCard = ({
  service,
  lang,
  onEditService,
  onDeleteService,
  onAddSchedule,
  onEditSchedule,
  onDeleteSchedule,
  resolveDayLabel,
  schedulesExpanded,
}) => (
  <div className="rounded-[22px] bg-white p-4 shadow-sm ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700">
    <div className="flex items-start gap-4">
      {service.image ? (
        <img src={service.image} alt={service.name || 'service'} className="h-16 w-16 rounded-2xl border object-cover" />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
          <Clock className="h-6 w-6" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                {service.name || (lang === 'ar' ? 'خدمة قابلة للحجز' : 'Bookable service')}
              </h3>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                {formatServicePrice(service.price, lang)}
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              {service.description?.trim() || (lang === 'ar' ? 'لا يوجد وصف مضاف حالياً.' : 'No description added yet.')}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-slate-500 dark:text-slate-300">
              <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
                {lang === 'ar' ? `${service.duration_minutes} دقيقة` : `${service.duration_minutes} min`}
              </span>
              {service.schedules?.length ? (
                <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
                  {lang === 'ar' ? `${service.schedules.length} جداول` : `${service.schedules.length} schedules`}
                </span>
              ) : null}
              <span className={`rounded-full px-3 py-1 ${service.is_active ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300'}`}>
                {service.is_active ? (lang === 'ar' ? 'مفعلة' : 'Active') : lang === 'ar' ? 'معطلة' : 'Inactive'}
              </span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onEditService}>
                <Pencil className="mr-2 h-4 w-4" />
                {lang === 'ar' ? 'تعديل الخدمة' : 'Edit service'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDeleteService} className="text-red-600 focus:text-red-600">
                <Trash2 className="mr-2 h-4 w-4" />
                {lang === 'ar' ? 'حذف الخدمة' : 'Delete service'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {schedulesExpanded ? (
          <div className="mt-4 rounded-[18px] bg-slate-50 p-4 dark:bg-slate-900/60">
            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={onAddSchedule}
                className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
              >
                <Plus className="mr-1 h-4 w-4" />
                {lang === 'ar' ? 'إضافة جدول' : 'Add schedule'}
              </Button>
              <div className="text-right">
                <p className="text-sm font-extrabold text-slate-900 dark:text-white">{lang === 'ar' ? 'جداول الخدمة' : 'Service schedules'}</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                  {service.schedules?.length
                    ? lang === 'ar'
                      ? `${service.schedules.length} جداول مضافة لهذه الخدمة`
                      : `${service.schedules.length} schedules added for this service`
                    : lang === 'ar'
                      ? 'لا توجد جداول مضافة بعد'
                      : 'No schedules added yet'}
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {service.schedules?.length ? (
                service.schedules.map((serviceSchedule, scheduleIndex) => (
                  <div
                    key={serviceSchedule.localId || `${service.localId}-${scheduleIndex}`}
                    className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => onEditSchedule(scheduleIndex)}
                          className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteSchedule(scheduleIndex)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="min-w-[260px] text-right">
                        <div className="text-sm font-bold text-slate-900 dark:text-white">{resolveDayLabel(serviceSchedule.day)}</div>
                        <div className="mt-2 space-y-2">
                          <TimeEdgeBadge label={lang === 'ar' ? 'من' : 'From'} value={formatDisplayTime(serviceSchedule.start, lang)} tone="start" />
                          <TimeEdgeBadge label={lang === 'ar' ? 'إلى' : 'To'} value={formatDisplayTime(serviceSchedule.end, lang)} tone="end" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-center text-sm font-medium text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {lang === 'ar' ? 'لا توجد جداول مضافة بعد' : 'No schedules added yet'}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

const DoctorSchedule = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = i18n.language;

  const daysOfWeek = useMemo(
    () => [
      { key: 'saturday', en: 'Saturday', ar: 'السبت' },
      { key: 'sunday', en: 'Sunday', ar: 'الأحد' },
      { key: 'monday', en: 'Monday', ar: 'الاثنين' },
      { key: 'tuesday', en: 'Tuesday', ar: 'الثلاثاء' },
      { key: 'wednesday', en: 'Wednesday', ar: 'الأربعاء' },
      { key: 'thursday', en: 'Thursday', ar: 'الخميس' },
      { key: 'friday', en: 'Friday', ar: 'الجمعة' },
    ],
    []
  );

  const [doctorId, setDoctorId] = useState(null);
  const [doctorTimezone, setDoctorTimezone] = useState(null);
  const [schedule, setSchedule] = useState(EMPTY_WEEKLY_SCHEDULE);
  const [slotDuration, setSlotDuration] = useState('30');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletedServiceIds, setDeletedServiceIds] = useState([]);
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [slotDialogSaving, setSlotDialogSaving] = useState(false);
  const [slotForm, setSlotForm] = useState(EMPTY_SLOT_FORM);
  const [slotDeleteId, setSlotDeleteId] = useState(null);
  const [serviceScheduleDialogOpen, setServiceScheduleDialogOpen] = useState(false);
  const [serviceScheduleForm, setServiceScheduleForm] = useState(EMPTY_SERVICE_SCHEDULE_FORM);
  const [expandedServiceLocalId, setExpandedServiceLocalId] = useState(null);
  const [createServiceDialogOpen, setCreateServiceDialogOpen] = useState(false);
  const [editingServiceLocalId, setEditingServiceLocalId] = useState(null);
  const [newServiceForm, setNewServiceForm] = useState(EMPTY_NEW_SERVICE_FORM);
  const [deleteServiceDialogOpen, setDeleteServiceDialogOpen] = useState(false);
  const [servicePendingDelete, setServicePendingDelete] = useState(null);

  const resolveDoctorIdFromUser = (currentUser) => {
    const partnerId = currentUser?.partner_id;
    if (typeof partnerId === 'number') return partnerId;
    if (typeof partnerId === 'string' && partnerId.trim() && !Number.isNaN(Number(partnerId))) {
      return Number(partnerId);
    }
    if (partnerId && typeof partnerId === 'object' && partnerId.id) {
      return Number(partnerId.id);
    }
    if (currentUser?.doctor?.id) {
      return Number(currentUser.doctor.id);
    }
    if (currentUser?.partner?.id) {
      return Number(currentUser.partner.id);
    }
    return null;
  };

  const activeAvailabilitySlots = useMemo(
    () =>
      daysOfWeek.flatMap((day) =>
        schedule[day.key].slots.map((slot, index) => ({
          ...slot,
          day: day.key,
          order: index,
          dayLabel: lang === 'ar' ? day.ar : day.en,
        }))
      ),
    [daysOfWeek, lang, schedule]
  );

  const activeSection = useMemo(() => {
    if (location.pathname.includes('/doctor/services')) return 'services';
    return 'availability';
  }, [location.pathname]);

  const allServices = useMemo(() => flattenServicesTree(services), [services]);

  const primaryServices = useMemo(
    () => allServices.filter(isPrimaryService),
    [allServices]
  );

  const parentServiceOptions = useMemo(
    () => {
      if (!editingServiceLocalId) {
        return allServices.filter((service) => service.id != null);
      }

      const currentService = findServiceByLocalId(services, editingServiceLocalId);
      if (!currentService) {
        return allServices.filter((service) => service.localId !== editingServiceLocalId && service.id != null);
      }

      const blockedIds = collectDescendantLocalIds(currentService);
      blockedIds.add(editingServiceLocalId);
      return allServices.filter((service) => !blockedIds.has(service.localId) && service.id != null);
    },
    [allServices, editingServiceLocalId, services]
  );

  const bookableServices = useMemo(
    () => collectBookableServices(services),
    [services]
  );

  const affectedServiceOptions = useMemo(() => {
    const editingService = allServices.find((service) => service.localId === editingServiceLocalId);

    return bookableServices.filter((service) => {
      if (service.localId === editingServiceLocalId || service.id == null) {
        return false;
      }

      if (editingService?.id && service.id === editingService.id) {
        return false;
      }

      return true;
    });
  }, [allServices, bookableServices, editingServiceLocalId]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const loadScheduleData = async (resolvedDoctorId, { includeDoctor = false, silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      let doctorData = null;
      let resolvedDoctorTimezone = extractDoctorTimezone(user);

      if (includeDoctor) {
        const doctorRes = await staffApi.getDoctor(resolvedDoctorId).catch(() => null);
        const doctorPayload = doctorRes?.data?.data || {};
        doctorData = doctorPayload?.doctor || doctorPayload;
        resolvedDoctorTimezone = extractDoctorTimezone(doctorData) || resolvedDoctorTimezone;
      }

      persistDoctorTimezoneOverride(resolvedDoctorTimezone);

      const [availabilityRes, servicesRes] = await Promise.all([
        staffApi.getAvailability(resolvedDoctorId).catch(() => null),
        staffApi.getServices(resolvedDoctorId).catch(() => null),
      ]);

      if (resolvedDoctorTimezone) {
        setDoctorTimezone(resolvedDoctorTimezone);
      }

      if (doctorData?.session_duration) {
        setSlotDuration(String(doctorData.session_duration));
      }

      const availabilityRows = availabilityRes?.data?.data || [];
      const normalizedSchedule = JSON.parse(JSON.stringify(EMPTY_WEEKLY_SCHEDULE));

      availabilityRows.forEach((row) => {
        const apiDayKey = row.day || WEEKDAY_TO_DAY[Number(row.day_of_week)] || null;
        if (!apiDayKey) return;

        const normalizedStart = convertApiScheduleToDoctorTime(apiDayKey, row.start_at || row.start, resolvedDoctorTimezone);
        const normalizedEnd = convertApiScheduleToDoctorTime(apiDayKey, row.end_at || row.end, resolvedDoctorTimezone);
        const dayKey = normalizedStart.day;
        if (!dayKey || !normalizedSchedule[dayKey]) return;

        normalizedSchedule[dayKey].enabled = true;
        normalizedSchedule[dayKey].slots.push({
          id: row.id,
          start: normalizedStart.time || '09:00',
          end: normalizedEnd.time || '17:00',
          availability_type: row.availability_type || (row.is_online ? 'online' : 'in_clinic'),
        });
      });

      setSchedule(normalizedSchedule);

      const servicesRows = servicesRes?.data?.data || [];
      const normalizedServices = servicesRows.map((service, index) =>
        mapServiceRow(
          service,
          index,
          doctorData?.session_duration || Number(slotDuration || 30),
          resolvedDoctorTimezone
        )
      );
      setServices(buildServicesTree(normalizedServices));
    } catch (error) {
      console.error('Failed to load doctor schedule data:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل بيانات الجدول' : 'Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      if (!isAuthenticated || !user) return;

      const resolvedDoctorId = resolveDoctorIdFromUser(user);
      if (!resolvedDoctorId) {
        toast.error(lang === 'ar' ? 'تعذر تحديد حساب الطبيب' : 'Unable to resolve doctor account');
        setLoading(false);
        return;
      }

      setDoctorId(resolvedDoctorId);
      await loadScheduleData(resolvedDoctorId, { includeDoctor: true });
    };

    bootstrap();
  }, [isAuthenticated, user, lang]);

  const addService = () => {
    setEditingServiceLocalId(null);
    setNewServiceForm({
      ...EMPTY_NEW_SERVICE_FORM,
      duration_minutes: slotDuration || '30',
    });
    setCreateServiceDialogOpen(true);
  };

  const openEditServiceDialog = (service) => {
    setEditingServiceLocalId(service.localId);
    setNewServiceForm({
      name: service.name || '',
      description: service.description || '',
      parent_id: service.parent_id ?? null,
      service_kind: service.service_kind || (service.parent_id ? 'bookable' : 'category'),
      duration_minutes: String(service.duration_minutes || slotDuration || '30'),
      price: String(service.price ?? '0'),
      is_active: service.is_active !== false,
      coming_soon: !!service.coming_soon,
      allow_multi_booking: !!service.allow_multi_booking,
      affects_general_appointments: !!service.affects_general_appointments,
      affected_service_ids: Array.isArray(service.affected_service_ids) ? [...service.affected_service_ids] : [],
      schedules: Array.isArray(service.schedules) ? [...service.schedules] : [],
      image: service.image_file || null,
      imagePreview: service.image || null,
      remove_image: !!service.remove_image,
    });
    setCreateServiceDialogOpen(true);
  };

  const handleCreateServiceDialogChange = (open) => {
    setCreateServiceDialogOpen(open);
    if (!open) {
      setEditingServiceLocalId(null);
      setNewServiceForm({
        ...EMPTY_NEW_SERVICE_FORM,
        duration_minutes: slotDuration || '30',
      });
    }
  };

  const buildServiceFromForm = () => ({
    parent_id: newServiceForm.parent_id ?? null,
    service_kind: newServiceForm.parent_id ? 'bookable' : newServiceForm.service_kind,
    name: newServiceForm.name.trim(),
    description: newServiceForm.description.trim(),
    duration_minutes: newServiceForm.service_kind === 'category' ? '0' : (newServiceForm.duration_minutes || slotDuration || '30'),
    price: newServiceForm.service_kind === 'category' ? '0' : (newServiceForm.price || '0'),
    is_active: !!newServiceForm.is_active,
    coming_soon: !!newServiceForm.coming_soon,
    allow_multi_booking: !!newServiceForm.allow_multi_booking,
    affects_general_appointments:
      !!newServiceForm.allow_multi_booking && !!newServiceForm.affects_general_appointments,
    affected_service_ids: newServiceForm.allow_multi_booking
      ? [...(newServiceForm.affected_service_ids || [])]
      : [],
    schedules: [...(newServiceForm.schedules || [])],
    image: newServiceForm.remove_image
      ? null
      : newServiceForm.image
        ? URL.createObjectURL(newServiceForm.image)
        : newServiceForm.imagePreview || null,
    has_media: newServiceForm.remove_image ? false : !!(newServiceForm.image || newServiceForm.imagePreview),
    image_file: newServiceForm.image || null,
    remove_image: !!newServiceForm.remove_image,
  });

  const saveNewService = async () => {
    if (!newServiceForm.name.trim()) {
      toast.error(lang === 'ar' ? 'يرجى إدخال اسم الخدمة' : 'Please enter a service name');
      return;
    }
    if (newServiceForm.service_kind === 'bookable' && Number(newServiceForm.duration_minutes) <= 0) {
      toast.error(lang === 'ar' ? 'مدة الخدمة الفرعية يجب أن تكون أكبر من صفر' : 'A bookable service requires a positive duration');
      return;
    }

    const draftService = buildServiceFromForm();
    const nextServices = editingServiceLocalId
      ? updateServiceInTree(services, editingServiceLocalId, (service) => ({
          ...service,
          ...draftService,
          image: draftService.image ?? service.image,
          has_media: draftService.remove_image ? false : !!(draftService.image || service.image),
        }))
      : [
          ...services,
          {
            localId: `new-${Date.now()}-${Math.random()}`,
            id: null,
            ...draftService,
            has_sub_service: false,
            children: [],
          },
        ];

    setServices(nextServices);
    handleCreateServiceDialogChange(false);
    await persistServices(nextServices);
  };

  const updateServiceSchedules = (serviceLocalId, updater) => {
    if (serviceLocalId === '__draft__') {
      setNewServiceForm((prev) => ({
        ...prev,
        schedules: updater(prev.schedules || []),
      }));
      return;
    }

    setServices((prev) =>
      updateServiceInTree(prev, serviceLocalId, (service) => ({
        ...service,
        schedules: updater(service.schedules || []),
      }))
    );
  };

  const requestDeleteService = (service) => {
    if (saving) return;
    setServicePendingDelete(service);
    setDeleteServiceDialogOpen(true);
  };

  const handleDeleteServiceDialogChange = (open) => {
    if (saving) return;
    setDeleteServiceDialogOpen(open);
    if (!open) {
      setServicePendingDelete(null);
    }
  };

  const removeService = async () => {
    const service = servicePendingDelete;
    if (!service) return;

    const previousServices = services;
    const nextServices = removeServiceFromTree(services, service.localId);
    const descendantLocalIds = collectDescendantLocalIds(service);
    const serviceId = service.id;

    setDeleteServiceDialogOpen(false);
    setServicePendingDelete(null);
    setServices(nextServices);
    if (expandedServiceLocalId === service.localId || descendantLocalIds.has(expandedServiceLocalId)) {
      setExpandedServiceLocalId(null);
    }

    if (!serviceId) {
      return;
    }

    if (!doctorId) {
      setDeletedServiceIds((prev) => uniqueIds([...prev, serviceId]));
      return;
    }

    setSaving(true);

    try {
      await staffApi.deleteService(serviceId);

      setDeletedServiceIds((prev) => prev.filter((deletedId) => deletedId !== serviceId));
      toast.success(lang === 'ar' ? 'تم حذف الخدمة بنجاح' : 'Service deleted successfully');
      await loadScheduleData(doctorId, { silent: true });
    } catch (error) {
      console.error('Failed to delete service:', error);
      if (doctorId) {
        await loadScheduleData(doctorId, { silent: true }).catch(() => {
          setServices(previousServices);
        });
      } else {
        setServices(previousServices);
      }
      const backendMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(
        lang === 'ar'
          ? `تعذر حذف الخدمة${backendMessage ? `: ${backendMessage}` : ''}`
          : `Failed to delete service${backendMessage ? `: ${backendMessage}` : ''}`
      );
    } finally {
      setSaving(false);
    }
  };

  const updateService = (localId, field, value) => {
    setServices((prev) => updateServiceInTree(prev, localId, { [field]: value }));
  };

  const handleServiceImageChange = (localId, file) => {
    setServices((prev) =>
      updateServiceInTree(prev, localId, (service) => ({
        ...service,
        image_file: file || null,
        image: file ? URL.createObjectURL(file) : service.image,
        has_media: file ? true : service.has_media,
        remove_image: false,
      }))
    );
  };

  const clearServiceImage = (localId) => {
    setServices((prev) =>
      updateServiceInTree(prev, localId, (service) => ({
        ...service,
        image: null,
        has_media: false,
        image_file: null,
        remove_image: !!service.id,
      }))
    );
  };

  const handleDraftServiceImageChange = (file) => {
    setNewServiceForm((prev) => ({
      ...prev,
      image: file || null,
      imagePreview: file ? URL.createObjectURL(file) : prev.imagePreview || null,
      remove_image: false,
    }));
  };

  const clearDraftServiceImage = () => {
    setNewServiceForm((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
      remove_image: true,
    }));
  };

  const resolveDayLabel = (dayKey) => {
    const day = daysOfWeek.find((item) => item.key === dayKey);
    return lang === 'ar' ? day?.ar || dayKey : day?.en || dayKey;
  };

  const toggleServiceSchedules = (serviceLocalId) => {
    setExpandedServiceLocalId((current) => (current === serviceLocalId ? null : serviceLocalId));
  };

  const openCreateServiceScheduleDialog = (serviceLocalId) => {
    setServiceScheduleForm({
      ...EMPTY_SERVICE_SCHEDULE_FORM,
      serviceLocalId,
    });
    setServiceScheduleDialogOpen(true);
  };

  const openEditServiceScheduleDialog = (serviceLocalId, scheduleIndex) => {
    const targetSchedules =
      serviceLocalId === '__draft__'
        ? newServiceForm.schedules || []
        : findServiceByLocalId(services, serviceLocalId)?.schedules || [];
    const targetSchedule = targetSchedules[scheduleIndex];
    if (!targetSchedule) return;

    setServiceScheduleForm({
      serviceLocalId,
      scheduleIndex,
      day: targetSchedule.day,
      start: targetSchedule.start,
      end: targetSchedule.end,
    });
    setServiceScheduleDialogOpen(true);
  };

  const handleServiceScheduleDialogChange = (open) => {
    setServiceScheduleDialogOpen(open);
    if (!open) {
      setServiceScheduleForm(EMPTY_SERVICE_SCHEDULE_FORM);
    }
  };

  const saveServiceSchedule = () => {
    if (!serviceScheduleForm.serviceLocalId) return;

    if (serviceScheduleForm.end <= serviceScheduleForm.start) {
      toast.error(lang === 'ar' ? 'يجب أن يكون وقت النهاية بعد وقت البداية' : 'End time must be after start time');
      return;
    }

    updateServiceSchedules(serviceScheduleForm.serviceLocalId, (currentSchedules) => {
      const nextSchedule = {
        localId:
          serviceScheduleForm.scheduleIndex !== null
            ? currentSchedules[serviceScheduleForm.scheduleIndex]?.localId || `schedule-${Date.now()}`
            : `schedule-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        day: serviceScheduleForm.day,
        start: serviceScheduleForm.start,
        end: serviceScheduleForm.end,
        is_online: false,
      };

      if (serviceScheduleForm.scheduleIndex !== null) {
        return currentSchedules.map((item, index) => (index === serviceScheduleForm.scheduleIndex ? nextSchedule : item));
      }

      return [...currentSchedules, nextSchedule];
    });

    handleServiceScheduleDialogChange(false);
  };

  const draftSchedules = newServiceForm.schedules || [];

  const deleteServiceSchedule = (serviceLocalId, scheduleIndex) => {
    updateServiceSchedules(serviceLocalId, (currentSchedules) => currentSchedules.filter((_, index) => index !== scheduleIndex));
  };

  const openCreateSlotDialog = () => {
    setSlotForm(EMPTY_SLOT_FORM);
    setSlotDialogOpen(true);
  };

  const openEditSlotDialog = (slot) => {
    setSlotForm({
      id: slot.id,
      day: slot.day,
      start: slot.start,
      end: slot.end,
      availability_type: slot.availability_type || 'in_clinic',
    });
    setSlotDialogOpen(true);
  };

  const handleSlotDialogChange = (open) => {
    if (slotDialogSaving) return;
    setSlotDialogOpen(open);
    if (!open) {
      setSlotForm(EMPTY_SLOT_FORM);
    }
  };

  const handleSlotSave = async () => {
    if (!doctorId) return;

    if (slotForm.end <= slotForm.start) {
      toast.error(lang === 'ar' ? 'يجب أن يكون وقت النهاية بعد وقت البداية' : 'End time must be after start time');
      return;
    }

    setSlotDialogSaving(true);

    try {
      const apiStart = convertDoctorTimeToApiSchedule(slotForm.day, slotForm.start, doctorTimezone);
      const apiEnd = convertDoctorTimeToApiSchedule(slotForm.day, slotForm.end, doctorTimezone);
      const payload = {
        doctor_id: doctorId,
        day: apiStart.day,
        availability_type: slotForm.availability_type,
        is_online: slotForm.availability_type === 'online',
        start_at: toApiTimeValue(apiStart.time, '09:00'),
        end_at: toApiTimeValue(apiEnd.time, '17:00'),
        is_active: true,
        timezone: doctorTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      };

      if (slotForm.id) {
        await staffApi.updateAvailability(slotForm.id, payload);
      } else {
        await staffApi.createAvailability(payload);
      }

      await loadScheduleData(doctorId, { silent: true });
      setSlotDialogOpen(false);
      setSlotForm(EMPTY_SLOT_FORM);
      toast.success(lang === 'ar' ? 'تم حفظ وقت التوفر بنجاح' : 'Availability saved successfully');
    } catch (error) {
      console.error('Failed to save availability slot:', error);
      const backendMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(
        lang === 'ar'
          ? `فشل في حفظ وقت التوفر${backendMessage ? `: ${backendMessage}` : ''}`
          : `Failed to save availability${backendMessage ? `: ${backendMessage}` : ''}`
      );
    } finally {
      setSlotDialogSaving(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!doctorId || !slotId) return;

    setSlotDeleteId(slotId);

    try {
      await staffApi.deleteAvailability(slotId);
      await loadScheduleData(doctorId, { silent: true });
      toast.success(lang === 'ar' ? 'تم حذف وقت التوفر بنجاح' : 'Availability deleted successfully');
    } catch (error) {
      console.error('Failed to delete availability slot:', error);
      const backendMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(
        lang === 'ar'
          ? `تعذر حذف وقت التوفر${backendMessage ? `: ${backendMessage}` : ''}`
          : `Failed to delete availability${backendMessage ? `: ${backendMessage}` : ''}`
      );
    } finally {
      setSlotDeleteId(null);
    }
  };

  const persistServices = async (servicesToPersist) => {
    if (!doctorId) return;
    setSaving(true);

    try {
      await staffApi.updateDoctor(doctorId, {
        session_duration: Number(slotDuration || 30),
      });

      const serviceIdsToDelete = uniqueIds(deletedServiceIds);

      for (const serviceId of serviceIdsToDelete) {
        await staffApi.deleteService(serviceId);
      }

      for (const service of flattenServicesTree(servicesToPersist)) {
        if (service.id && serviceIdsToDelete.includes(service.id)) continue;
        if (!service.name?.trim()) continue;

        const payload = {
          doctor_id: doctorId,
          parent_id: service.parent_id ?? null,
          service_kind: service.service_kind || (service.parent_id ? 'bookable' : 'category'),
          name: service.name.trim(),
          description: service.description?.trim() || '',
          duration_minutes: service.service_kind === 'category' ? 0 : Number(service.duration_minutes || slotDuration || 30),
          price: service.service_kind === 'category' ? 0 : Number(service.price || 0),
          is_active: !!service.is_active,
          coming_soon: !!service.coming_soon,
          allow_multi_booking: !!service.allow_multi_booking,
          affects_general_appointments: !!service.allow_multi_booking && !!service.affects_general_appointments,
          affected_service_ids: service.allow_multi_booking ? service.affected_service_ids || [] : [],
          schedules: Array.isArray(service.schedules)
            ? service.schedules.map((serviceSchedule) => {
                const apiStart = convertDoctorTimeToApiSchedule(
                  serviceSchedule.day,
                  serviceSchedule.start,
                  doctorTimezone
                );
                const apiEnd = convertDoctorTimeToApiSchedule(
                  serviceSchedule.day,
                  serviceSchedule.end,
                  doctorTimezone
                );

                return {
                  day_of_week: DAY_TO_SERVICE_SCHEDULE_WEEKDAY[apiStart.day] ?? 0,
                  start_time: toApiTimeValue(apiStart.time, '09:00'),
                  end_time: toApiTimeValue(apiEnd.time, '17:00'),
                  is_online: !!serviceSchedule.is_online,
                };
              })
            : [],
          timezone: doctorTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          remove_image: !!service.remove_image,
        };

        if (service.image_file instanceof File) {
          payload.image_file = service.image_file;
        }

        if (service.id) {
          await staffApi.updateService(service.id, payload);
        } else {
          await staffApi.createService(payload);
        }
      }

      setDeletedServiceIds([]);
      toast.success(lang === 'ar' ? 'تم حفظ الخدمات ومدة الجلسة بنجاح' : 'Services and session duration saved successfully');
      await loadScheduleData(doctorId, { silent: true });
    } catch (error) {
      console.error('Failed to save schedule/services:', error);
      const backendMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(
        lang === 'ar'
          ? `فشل في حفظ البيانات${backendMessage ? `: ${backendMessage}` : ''}`
          : `Failed to save data${backendMessage ? `: ${backendMessage}` : ''}`
      );
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (activeSection === 'services') {
      await persistServices(services);
      return;
    }

    if (!doctorId) return;
    setSaving(true);

    try {
      await staffApi.updateDoctor(doctorId, {
        session_duration: Number(slotDuration || 30),
      });

      const serviceIdsToDelete = uniqueIds(deletedServiceIds);

      for (const serviceId of serviceIdsToDelete) {
        await staffApi.deleteService(serviceId);
      }

      for (const service of flattenServicesTree(services)) {
        if (service.id && serviceIdsToDelete.includes(service.id)) continue;
        if (!service.name?.trim()) continue;

        const payload = {
          doctor_id: doctorId,
          parent_id: service.parent_id ?? null,
          service_kind: service.service_kind || (service.parent_id ? 'bookable' : 'category'),
          name: service.name.trim(),
          description: service.description?.trim() || '',
          duration_minutes: service.service_kind === 'category' ? 0 : Number(service.duration_minutes || slotDuration || 30),
          price: service.service_kind === 'category' ? 0 : Number(service.price || 0),
          is_active: !!service.is_active,
          coming_soon: !!service.coming_soon,
          allow_multi_booking: !!service.allow_multi_booking,
          affects_general_appointments: !!service.allow_multi_booking && !!service.affects_general_appointments,
          affected_service_ids: service.allow_multi_booking ? service.affected_service_ids || [] : [],
          schedules: Array.isArray(service.schedules)
            ? service.schedules.map((serviceSchedule) => {
                const apiStart = convertDoctorTimeToApiSchedule(
                  serviceSchedule.day,
                  serviceSchedule.start,
                  doctorTimezone
                );
                const apiEnd = convertDoctorTimeToApiSchedule(
                  serviceSchedule.day,
                  serviceSchedule.end,
                  doctorTimezone
                );

                return {
                  day_of_week: DAY_TO_SERVICE_SCHEDULE_WEEKDAY[apiStart.day] ?? 0,
                  start_time: toApiTimeValue(apiStart.time, '09:00'),
                  end_time: toApiTimeValue(apiEnd.time, '17:00'),
                  is_online: !!serviceSchedule.is_online,
                };
              })
            : [],
          timezone: doctorTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          remove_image: !!service.remove_image,
        };

        if (service.image_file instanceof File) {
          payload.image_file = service.image_file;
        }

        if (service.id) {
          await staffApi.updateService(service.id, payload);
        } else {
          await staffApi.createService(payload);
        }
      }

      setDeletedServiceIds([]);
      toast.success(lang === 'ar' ? 'تم حفظ الخدمات ومدة الجلسة بنجاح' : 'Services and session duration saved successfully');
      await loadScheduleData(doctorId, { silent: true });
    } catch (error) {
      console.error('Failed to save schedule/services:', error);
      const backendMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(
        lang === 'ar'
          ? `فشل في حفظ البيانات${backendMessage ? `: ${backendMessage}` : ''}`
          : `Failed to save data${backendMessage ? `: ${backendMessage}` : ''}`
      );
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-schedule">
      <div className="border-b bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'إدارة التوفر والخدمات' : 'Availability & Services'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {activeSection === 'services'
                    ? lang === 'ar'
                      ? 'الخدمات منفصلة عن ساعات التوفر بنفس منطق التطبيق.'
                      : 'Services are separated from availability with the same mobile-app logic.'
                    : lang === 'ar'
                      ? 'هذه الشاشة مخصصة فقط لساعات التوفر العامة للطبيب.'
                      : 'This view is dedicated to the doctor general availability only.'}
                </p>
              </div>
            </div>
            {false && (
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSave} disabled={saving || loading}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? (lang === 'ar' ? 'جارٍ الحفظ...' : 'Saving...') : lang === 'ar' ? 'حفظ' : 'Save'}
            </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl space-y-6 px-4 py-6">
        <Card className="border-0 bg-gradient-to-br from-white via-teal-50 to-cyan-50 shadow-sm dark:from-slate-800 dark:via-slate-800 dark:to-slate-900">
          <CardContent className="flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="text-right">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-300">
                {lang === 'ar' ? 'بوابة الطبيب' : 'Doctor portal'}
              </p>
              <h2 className="mt-2 text-2xl font-black text-slate-900 dark:text-white">
                {activeSection === 'services'
                  ? lang === 'ar'
                    ? 'الخدمات'
                    : 'Services'
                  : lang === 'ar'
                    ? 'ساعات التوفر'
                    : 'Availability'}
              </h2>
              {activeSection !== 'services' && (
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {lang === 'ar'
                    ? 'أضف أو عدل أو احذف ساعات العمل بنفس أسلوب التطبيق، من دون خلطها مع إدارة الخدمات.'
                    : 'Add, edit, or remove working hours with the same app-like flow, without mixing them with service management.'}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SectionToggleButton
                active={activeSection === 'availability'}
                icon={Calendar}
                label={lang === 'ar' ? 'ساعات التوفر' : 'Availability'}
                onClick={() => navigate('/doctor/availability')}
              />
              <SectionToggleButton
                active={activeSection === 'services'}
                icon={Stethoscope}
                label={lang === 'ar' ? 'الخدمات' : 'Services'}
                onClick={() => navigate('/doctor/services')}
              />
            </div>
          </CardContent>
        </Card>
        {activeSection === 'availability' && (
        <>
        <Card className="border border-slate-100 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="h-5 w-5" />
                  {lang === 'ar' ? 'إدارة ساعات التوفر' : 'Manage Availability'}
                </CardTitle>
                <CardDescription>
                  {lang === 'ar' ? 'أضف أو عدل أو احذف أيام وساعات العمل بنفس أسلوب التطبيق' : 'Add, edit, or delete working-hour slots with the same flow as the mobile app'}
                </CardDescription>
              </div>
              <Button type="button" onClick={openCreateSlotDialog} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="mr-1 h-4 w-4" />
                {lang === 'ar' ? 'إضافة وقت' : 'Add time'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeAvailabilitySlots.length === 0 ? (
              <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                <Calendar className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                <p className="font-medium">{lang === 'ar' ? 'لا توجد ساعات توفر مضافة حالياً' : 'No availability slots added yet'}</p>
              </div>
            ) : (
              activeAvailabilitySlots.map((slot) => (
                <div
                  key={`${slot.day}-${slot.id || slot.order}`}
                  className="rounded-[24px] bg-white p-5 shadow-sm ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="rounded-2xl bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">
                      {lang === 'ar' ? 'في العيادة' : 'In clinic'}
                    </div>
                    <div className="min-w-[280px] text-right">
                      <p className="text-xl font-bold text-indigo-600 dark:text-indigo-300">{slot.dayLabel}</p>
                      <div className="mt-3 space-y-3">
                        <TimeEdgeBadge
                          label={lang === 'ar' ? 'من' : 'From'}
                          value={formatDisplayTime(slot.start, lang)}
                          tone="start"
                        />
                        <TimeEdgeBadge
                          label={lang === 'ar' ? 'إلى' : 'To'}
                          value={formatDisplayTime(slot.end, lang)}
                          tone="end"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center gap-3">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleDeleteSlot(slot.id)}
                      disabled={slotDeleteId === slot.id}
                      className="text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      {slotDeleteId === slot.id ? (lang === 'ar' ? 'جارٍ الحذف...' : 'Deleting...') : lang === 'ar' ? 'حذف' : 'Delete'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => openEditSlotDialog(slot)}
                      className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <Pencil className="mr-1 h-4 w-4" />
                      {lang === 'ar' ? 'تعديل' : 'Edit'}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        </>
        )}

        {activeSection === 'services' && (
        <>
        <Card className="border-0 shadow-sm dark:bg-slate-800">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-right">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Stethoscope className="h-5 w-5" />
                {lang === 'ar' ? 'الخدمات' : 'Services'}
              </CardTitle>
              <CardDescription>
                {lang === 'ar'
                  ? 'أضف خدمة جديدة من هنا ثم رتبها كخدمة رئيسية أو قابلة للحجز.'
                  : 'Add a service here, then organize it as a main or bookable service.'}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addService} className="w-full sm:w-auto">
              <Plus className="mr-1 h-4 w-4" />
              {lang === 'ar' ? 'إضافة خدمة' : 'Add Service'}
            </Button>
          </CardHeader>
          <CardContent className="grid gap-4 px-5 pb-5 pt-0 md:grid-cols-3">
            <div className="rounded-[22px] bg-white p-4 ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">{lang === 'ar' ? 'الخدمات الرئيسية' : 'Main services'}</p>
              <p className="mt-2 text-3xl font-black text-slate-900 dark:text-white">{primaryServices.length}</p>
            </div>
            <div className="rounded-[22px] bg-white p-4 ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">{lang === 'ar' ? 'القابلة للحجز' : 'Bookable services'}</p>
              <p className="mt-2 text-3xl font-black text-slate-900 dark:text-white">{bookableServices.length}</p>
            </div>
            <div className="rounded-[22px] bg-white p-4 ring-1 ring-slate-100 dark:bg-slate-900/70 dark:ring-slate-700">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-300">{lang === 'ar' ? 'مدة الجلسة الافتراضية' : 'Default session'}</p>
              <p className="mt-2 text-3xl font-black text-slate-900 dark:text-white">{slotDuration || '30'}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Stethoscope className="h-5 w-5" />
              {lang === 'ar' ? 'الخدمات الرئيسية' : 'Main services'}
            </CardTitle>
            <CardDescription>
              {lang === 'ar'
                ? 'الخدمة التي لا تملك جدولاً ولديها خدمات فرعية تظهر هنا كخدمة رئيسية.'
                : 'Services without schedules that have sub services appear here as main services.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {primaryServices.length === 0 ? (
              <div className="rounded-[24px] bg-slate-50 p-8 text-center text-slate-500 dark:bg-slate-900/60 dark:text-slate-300">
                <Stethoscope className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                <p className="font-medium">{lang === 'ar' ? 'لا توجد خدمات رئيسية حالياً' : 'No primary services yet'}</p>
              </div>
            ) : (
              primaryServices.map((service) => (
                <ServiceOverviewCard
                  key={`overview-${service.localId}`}
                  service={service}
                  lang={lang}
                  onEditService={() => openEditServiceDialog(service)}
                  onDeleteService={() => requestDeleteService(service)}
                />
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5" />
              {lang === 'ar' ? 'الخدمات القابلة للحجز' : 'Bookable services'}
            </CardTitle>
            {false && (
              <CardDescription>
                {lang === 'ar'
                  ? 'هذه هي الخدمات النهائية التي يمكن للمريض الحجز عليها مباشرة.'
                  : 'These are the final services a patient can book directly.'}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {bookableServices.length === 0 ? (
              <div className="rounded-[24px] bg-slate-50 p-8 text-center text-slate-500 dark:bg-slate-900/60 dark:text-slate-300">
                <Clock className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                <p className="font-medium">{lang === 'ar' ? 'لا توجد خدمات حالياً' : 'No services yet'}</p>
              </div>
            ) : (
              bookableServices.map((service) => (
                <BookableServiceManageCard
                  key={`bookable-${service.localId}`}
                  service={service}
                  lang={lang}
                  resolveDayLabel={resolveDayLabel}
                  schedulesExpanded={expandedServiceLocalId === service.localId}
                  onEditService={() => openEditServiceDialog(service)}
                  onDeleteService={() => requestDeleteService(service)}
                  onAddSchedule={() => openCreateServiceScheduleDialog(service.localId)}
                  onEditSchedule={(scheduleIndex) => openEditServiceScheduleDialog(service.localId, scheduleIndex)}
                  onDeleteSchedule={(scheduleIndex) => deleteServiceSchedule(service.localId, scheduleIndex)}
                />
              ))
            )}
          </CardContent>
        </Card>

        {false && <Card className="border-0 shadow-sm dark:bg-slate-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Stethoscope className="h-5 w-5" />
                {lang === 'ar' ? 'إدارة الخدمات' : 'Manage services'}
              </CardTitle>
              <CardDescription>
                {lang === 'ar'
                  ? 'حرر بيانات الخدمات وجداولها الخاصة بشكل مستقل عن ساعات التوفر العامة.'
                  : 'Edit service details and their own schedules independently from the general availability.'}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addService}>
              <Plus className="mr-1 h-4 w-4" />
              {lang === 'ar' ? 'إضافة خدمة' : 'Add Service'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {services.length === 0 ? (
              <div className="rounded-[24px] bg-slate-50 p-8 text-center text-slate-500 dark:bg-slate-900/60 dark:text-slate-300">
                <Stethoscope className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                <p className="font-medium">{lang === 'ar' ? 'لا توجد خدمات بعد' : 'No services yet'}</p>
              </div>
            ) : (
              services.map((service) => (
                <ServiceManageCard
                  key={`manage-${service.localId}`}
                  service={service}
                  lang={lang}
                  resolveDayLabel={resolveDayLabel}
                  onEditService={() => openEditServiceDialog(service)}
                  onDeleteService={() => requestDeleteService(service)}
                  onAddSchedule={() => openCreateServiceScheduleDialog(service.localId)}
                  onEditSchedule={(scheduleIndex) => openEditServiceScheduleDialog(service.localId, scheduleIndex)}
                  onDeleteSchedule={(scheduleIndex) => deleteServiceSchedule(service.localId, scheduleIndex)}
                />
              ))
            )}

            {false && services.map((service) => (
              <div key={service.localId} className="space-y-3 rounded-lg border p-4">
                <div className="grid gap-3 md:grid-cols-4">
                  <div className="md:col-span-2">
                    <Label>{lang === 'ar' ? 'اسم الخدمة' : 'Service Name'}</Label>
                    <Input
                      value={service.name}
                      onChange={(e) => updateService(service.localId, 'name', e.target.value)}
                      placeholder={lang === 'ar' ? 'مثال: استشارة أولية' : 'e.g. Initial Consultation'}
                    />
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'المدة (دقيقة)' : 'Duration (min)'}</Label>
                    <Input
                      type="number"
                      min="5"
                      value={service.duration_minutes}
                      onChange={(e) => updateService(service.localId, 'duration_minutes', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'السعر' : 'Price'}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={service.price}
                      onChange={(e) => updateService(service.localId, 'price', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label>{lang === 'ar' ? 'صورة الخدمة' : 'Service Image'}</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleServiceImageChange(service.localId, e.target.files?.[0] || null)}
                    />
                  </div>

                  {service.image ? (
                    <div className="flex items-center gap-3 rounded-lg border p-3">
                      <img src={service.image} alt={service.name || 'service'} className="h-16 w-16 rounded-lg border object-cover" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => clearServiceImage(service.localId)}
                        className="text-red-500 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        {lang === 'ar' ? 'إزالة الصورة' : 'Remove image'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <ImageIcon className="h-4 w-4" />
                      <span>{lang === 'ar' ? 'لا توجد صورة لهذه الخدمة' : 'No image selected for this service'}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch checked={service.is_active} onCheckedChange={(checked) => updateService(service.localId, 'is_active', checked)} />
                    <Label>{lang === 'ar' ? 'الخدمة مفعلة' : 'Service Active'}</Label>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => requestDeleteService(service)}
                    className="text-red-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    {lang === 'ar' ? 'حذف' : 'Delete'}
                  </Button>
                </div>

                <div className="rounded-[18px] bg-slate-50 p-4 dark:bg-slate-900/60">
                  <div className="flex items-center justify-between">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => openCreateServiceScheduleDialog(service.localId)}
                      className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      {lang === 'ar' ? 'إضافة' : 'Add'}
                    </Button>
                    <div className="text-right">
                      <p className="text-base font-extrabold text-slate-900 dark:text-white">{lang === 'ar' ? 'الجداول' : 'Schedules'}</p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                        {service.schedules?.length
                          ? lang === 'ar'
                            ? 'هذه الجداول تحدد ساعات العمل الخاصة بهذه الخدمة.'
                            : 'These schedules belong to the service itself.'
                          : lang === 'ar'
                            ? 'إذا لم تضف جدولاً هنا فسيتم استخدام جدول الطبيب العام لهذه الخدمة.'
                            : 'If you leave them empty, the service can rely on the general availability.'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {service.schedules?.length ? (
                      service.schedules.map((serviceSchedule, scheduleIndex) => {
                        const dayLabel = daysOfWeek.find((day) => day.key === serviceSchedule.day);

                        return (
                          <div
                            key={serviceSchedule.localId || `${service.localId}-${scheduleIndex}`}
                            className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                          >
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteServiceSchedule(service.localId, scheduleIndex)}
                                  className="text-red-500 hover:bg-red-50 hover:text-red-600"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditServiceScheduleDialog(service.localId, scheduleIndex)}
                                  className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="min-w-[280px] text-right">
                                <p className="font-bold text-slate-900 dark:text-white">
                                  {lang === 'ar' ? dayLabel?.ar || serviceSchedule.day : dayLabel?.en || serviceSchedule.day}
                                </p>
                                <div className="mt-3 space-y-3">
                                  <TimeEdgeBadge
                                    label={lang === 'ar' ? 'من' : 'From'}
                                    value={formatDisplayTime(serviceSchedule.start, lang)}
                                    tone="start"
                                  />
                                  <TimeEdgeBadge
                                    label={lang === 'ar' ? 'إلى' : 'To'}
                                    value={formatDisplayTime(serviceSchedule.end, lang)}
                                    tone="end"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-center text-sm font-medium text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {lang === 'ar' ? 'لا توجد جداول مضافة بعد' : 'No schedules added yet'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>}
        </>
        )}

        {/*
        <Card className="border-0 bg-blue-50 shadow-sm dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-800">{lang === 'ar' ? 'ملاحظة' : 'Note'}</p>
                <p className="text-sm text-blue-700">
                  {activeSection === 'services'
                    ? lang === 'ar'
                      ? 'تؤثر تحديثات الخدمات على الحجوزات الجديدة فقط، مع بقاء التوفر العام منفصلاً عنها.'
                      : 'Service updates affect only new bookings, while the general availability remains separate.'
                    : lang === 'ar'
                      ? 'تؤثر تحديثات ساعات التوفر على الحجوزات الجديدة فقط، ولن تعدل المواعيد المحجوزة مسبقاً.'
                      : 'Availability updates affect only new bookings and do not alter existing appointments.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        */}
      </div>

      <AlertDialog open={deleteServiceDialogOpen} onOpenChange={handleDeleteServiceDialogChange}>
        <AlertDialogContent className="border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white">
          <AlertDialogHeader className="text-right sm:text-right">
            <AlertDialogTitle>
              {lang === 'ar' ? 'حذف الخدمة' : 'Delete service'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lang === 'ar'
                ? `هل تريد حذف خدمة "${servicePendingDelete?.name || ''}"؟`
                : `Do you want to delete "${servicePendingDelete?.name || 'this service'}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={saving}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={removeService}
              disabled={saving}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {saving
                ? lang === 'ar'
                  ? 'جارٍ الحذف...'
                  : 'Deleting...'
                : lang === 'ar'
                  ? 'حذف'
                  : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={slotDialogOpen} onOpenChange={handleSlotDialogChange}>
        <DialogContent className="border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white sm:max-w-xl">
          <DialogHeader className="text-right sm:text-right">
            <DialogTitle>{slotForm.id ? (lang === 'ar' ? 'تعديل التوفر' : 'Edit availability') : lang === 'ar' ? 'إضافة توفر' : 'Add availability'}</DialogTitle>
            <DialogDescription>
              {lang === 'ar' ? 'اختر اليوم ووقت البداية والنهاية بنفس أسلوب التطبيق.' : 'Choose the day, start time, and end time just like the mobile app.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{lang === 'ar' ? 'نوع التوفر' : 'Availability type'}</Label>
              <Select value={slotForm.availability_type} onValueChange={(value) => setSlotForm((prev) => ({ ...prev, availability_type: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_clinic">{lang === 'ar' ? 'في العيادة' : 'At clinic'}</SelectItem>
                  <SelectItem value="online">{lang === 'ar' ? 'أونلاين' : 'Online'}</SelectItem>
                  <SelectItem value="home_visit">{lang === 'ar' ? 'زيارة منزلية' : 'Home visit'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{lang === 'ar' ? 'اليوم' : 'Day'}</Label>
              <Select value={slotForm.day} onValueChange={(value) => setSlotForm((prev) => ({ ...prev, day: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map((day) => (
                    <SelectItem key={day.key} value={day.key}>
                      {lang === 'ar' ? day.ar : day.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'يبدأ في' : 'Starts at'}</Label>
                <Select value={slotForm.start} onValueChange={(value) => setSlotForm((prev) => ({ ...prev, start: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((time) => (
                      <SelectItem key={`start-${time}`} value={time}>
                        {formatDisplayTime(time, lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'ينتهي في' : 'Ends at'}</Label>
                <Select value={slotForm.end} onValueChange={(value) => setSlotForm((prev) => ({ ...prev, end: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((time) => (
                      <SelectItem key={`end-${time}`} value={time}>
                        {formatDisplayTime(time, lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => handleSlotDialogChange(false)} disabled={slotDialogSaving}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="button" onClick={handleSlotSave} disabled={slotDialogSaving} className="bg-indigo-600 hover:bg-indigo-700">
              {slotDialogSaving
                ? lang === 'ar'
                  ? 'جارٍ الحفظ...'
                  : 'Saving...'
                : slotForm.id
                  ? lang === 'ar'
                    ? 'تحديث التوفر'
                    : 'Update availability'
                  : lang === 'ar'
                    ? 'حفظ التوفر'
                    : 'Save availability'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={serviceScheduleDialogOpen} onOpenChange={handleServiceScheduleDialogChange}>
        <DialogContent className="border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white sm:max-w-xl">
          <DialogHeader className="text-right sm:text-right">
            <DialogTitle>
              {serviceScheduleForm.scheduleIndex !== null
                ? lang === 'ar'
                  ? 'تعديل جدول الخدمة'
                  : 'Edit service schedule'
                : lang === 'ar'
                  ? 'إضافة جدول للخدمة'
                  : 'Add service schedule'}
            </DialogTitle>
            <DialogDescription>
              {lang === 'ar' ? 'حدد ساعات العمل الخاصة بهذه الخدمة.' : 'Set the working hours for this service.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{lang === 'ar' ? 'اليوم' : 'Day'}</Label>
              <Select
                value={serviceScheduleForm.day}
                onValueChange={(value) => setServiceScheduleForm((prev) => ({ ...prev, day: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map((day) => (
                    <SelectItem key={`service-day-${day.key}`} value={day.key}>
                      {lang === 'ar' ? day.ar : day.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'يبدأ في' : 'Starts at'}</Label>
                <Select
                  value={serviceScheduleForm.start}
                  onValueChange={(value) => setServiceScheduleForm((prev) => ({ ...prev, start: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((time) => (
                      <SelectItem key={`service-start-${time}`} value={time}>
                        {formatDisplayTime(time, lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'ينتهي في' : 'Ends at'}</Label>
                <Select
                  value={serviceScheduleForm.end}
                  onValueChange={(value) => setServiceScheduleForm((prev) => ({ ...prev, end: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((time) => (
                      <SelectItem key={`service-end-${time}`} value={time}>
                        {formatDisplayTime(time, lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => handleServiceScheduleDialogChange(false)}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="button" onClick={saveServiceSchedule} className="bg-indigo-600 hover:bg-indigo-700">
              {serviceScheduleForm.scheduleIndex !== null
                ? lang === 'ar'
                  ? 'تحديث الجدول'
                  : 'Update schedule'
                : lang === 'ar'
                  ? 'إضافة الجدول'
                  : 'Add schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createServiceDialogOpen} onOpenChange={handleCreateServiceDialogChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white sm:max-w-2xl">
          <DialogHeader className="text-right sm:text-right">
            <DialogTitle>
              {editingServiceLocalId
                ? lang === 'ar'
                  ? 'تعديل الخدمة'
                  : 'Edit service'
                : lang === 'ar'
                  ? 'إنشاء خدمة جديدة'
                  : 'Create new service'}
            </DialogTitle>
            <DialogDescription>
              {editingServiceLocalId
                ? lang === 'ar'
                  ? 'حدّث بيانات الخدمة من نافذة مستقلة مع إبقاء الجداول ضمن البطاقة نفسها.'
                  : 'Update the service details in a separate dialog while keeping schedules on the card.'
                : lang === 'ar'
                  ? 'أضف بيانات الخدمة الأساسية من نافذة مستقلة للحفاظ على ترتيب الصفحة.'
                  : 'Add the core service details in a separate dialog to keep the page clean.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-[24px] bg-slate-50 p-4 dark:bg-slate-800">
              <div className="space-y-3">
                <Label className="text-base font-extrabold">{lang === 'ar' ? 'صورة الخدمة' : 'Service image'}</Label>
                <div className="overflow-hidden rounded-[20px] bg-indigo-50 dark:bg-indigo-500/10">
                  {newServiceForm.imagePreview ? (
                    <img
                      src={newServiceForm.imagePreview}
                      alt={newServiceForm.name || 'service'}
                      className="h-56 w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-56 items-center justify-center text-indigo-500 dark:text-indigo-300">
                      <div className="flex flex-col items-center gap-3">
                        <ImageIcon className="h-12 w-12" />
                        <span className="text-sm font-semibold">
                          {lang === 'ar' ? 'لا توجد صورة مضافة' : 'No image selected'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3">
                  {newServiceForm.imagePreview ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearDraftServiceImage}
                      className="text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      {lang === 'ar' ? 'حذف' : 'Remove'}
                    </Button>
                  ) : (
                    <span className="text-sm text-slate-500">{lang === 'ar' ? 'يمكنك رفع صورة لهذه الخدمة' : 'You can upload a service image'}</span>
                  )}
                  <label htmlFor="create-service-image-input-top">
                    <span className="inline-flex cursor-pointer items-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">
                      <ImageIcon className="mr-2 h-4 w-4" />
                      {newServiceForm.imagePreview
                        ? lang === 'ar'
                          ? 'تغيير الصورة'
                          : 'Change image'
                        : lang === 'ar'
                          ? 'اختيار صورة'
                          : 'Choose image'}
                    </span>
                  </label>
                  <Input
                    id="create-service-image-input-top"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleDraftServiceImageChange(e.target.files?.[0] || null)}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{lang === 'ar' ? 'الاسم' : 'Name'}</Label>
              <Input
                value={newServiceForm.name}
                onChange={(e) => setNewServiceForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={lang === 'ar' ? 'مثال: استشارة أولية' : 'e.g. Initial consultation'}
              />
            </div>

            <div className="space-y-2">
              <Label>{lang === 'ar' ? 'الوصف' : 'Description'}</Label>
              <textarea
                value={newServiceForm.description}
                onChange={(e) => setNewServiceForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={lang === 'ar' ? 'وصف مختصر للخدمة' : 'Short service description'}
                rows={3}
                className="flex min-h-[110px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <div className="space-y-2">
              <Label>{lang === 'ar' ? 'الخدمة الرئيسية' : 'Parent service'}</Label>
              <Select
                value={newServiceForm.parent_id == null ? 'none' : String(newServiceForm.parent_id)}
                onValueChange={(value) =>
                  setNewServiceForm((prev) => ({
                    ...prev,
                    parent_id: value === 'none' ? null : Number(value),
                    service_kind: value === 'none' ? 'category' : 'bookable',
                    duration_minutes:
                      !prev.duration_minutes || prev.duration_minutes === '0' ? slotDuration || '30' : prev.duration_minutes,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{lang === 'ar' ? 'بدون خدمة رئيسية' : 'No parent service'}</SelectItem>
                  {parentServiceOptions.map((service) => (
                    <SelectItem key={`parent-${service.localId}`} value={String(service.id)}>
                      {service.name || (lang === 'ar' ? 'خدمة بدون اسم' : 'Untitled service')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newServiceForm.service_kind === 'bookable' && <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'المدة بالدقائق' : 'Duration in minutes'}</Label>
                <Input
                  type="number"
                  min="1"
                  value={newServiceForm.duration_minutes}
                  onChange={(e) => setNewServiceForm((prev) => ({ ...prev, duration_minutes: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>{lang === 'ar' ? 'السعر' : 'Price'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newServiceForm.price}
                  onChange={(e) => setNewServiceForm((prev) => ({ ...prev, price: e.target.value }))}
                />
              </div>
            </div>}

            {false && <div className="rounded-[24px] bg-slate-50 p-4 dark:bg-slate-800">
              <div className="space-y-3">
                <Label className="text-base font-extrabold">{lang === 'ar' ? 'صورة الخدمة' : 'Service image'}</Label>
                <div className="overflow-hidden rounded-[20px] bg-indigo-50 dark:bg-indigo-500/10">
                  {newServiceForm.imagePreview ? (
                    <img
                      src={newServiceForm.imagePreview}
                      alt={newServiceForm.name || 'service'}
                      className="h-56 w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-56 items-center justify-center text-indigo-500 dark:text-indigo-300">
                      <div className="flex flex-col items-center gap-3">
                        <ImageIcon className="h-12 w-12" />
                        <span className="text-sm font-semibold">
                          {lang === 'ar' ? 'لا توجد صورة مضافة' : 'No image selected'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3">
                  {newServiceForm.imagePreview ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearDraftServiceImage}
                      className="text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      {lang === 'ar' ? 'حذف' : 'Remove'}
                    </Button>
                  ) : (
                    <span className="text-sm text-slate-500">{lang === 'ar' ? 'يمكنك رفع صورة لهذه الخدمة' : 'You can upload a service image'}</span>
                  )}
                  <label htmlFor="create-service-image-input">
                    <span className="inline-flex cursor-pointer items-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">
                      <ImageIcon className="mr-2 h-4 w-4" />
                      {newServiceForm.imagePreview
                        ? lang === 'ar'
                          ? 'تغيير الصورة'
                          : 'Change image'
                        : lang === 'ar'
                          ? 'اختيار صورة'
                          : 'Choose image'}
                    </span>
                  </label>
                  <Input
                    id="create-service-image-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleDraftServiceImageChange(e.target.files?.[0] || null)}
                  />
                </div>
              </div>
            </div>}

            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
              <Label>{lang === 'ar' ? 'الخدمة مفعلة' : 'Service active'}</Label>
              <Switch
                checked={newServiceForm.is_active}
                onCheckedChange={(checked) => setNewServiceForm((prev) => ({ ...prev, is_active: checked }))}
              />
            </div>

            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
              <Label>{lang === 'ar' ? 'قريباً' : 'Coming soon'}</Label>
              <Switch
                checked={newServiceForm.coming_soon}
                onCheckedChange={(checked) => setNewServiceForm((prev) => ({ ...prev, coming_soon: checked }))}
              />
            </div>

            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
              <Label>{lang === 'ar' ? 'السماح بالحجز المتعدد' : 'Allow multi booking'}</Label>
              <Switch
                checked={newServiceForm.allow_multi_booking}
                onCheckedChange={(checked) =>
                  setNewServiceForm((prev) => ({
                    ...prev,
                    allow_multi_booking: checked,
                    affects_general_appointments: checked ? prev.affects_general_appointments : false,
                    affected_service_ids: checked ? prev.affected_service_ids : [],
                  }))
                }
              />
            </div>

            <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
              <Label className={!newServiceForm.allow_multi_booking ? 'text-slate-400' : ''}>
                {lang === 'ar' ? 'يؤثر على المواعيد العامة' : 'Affects general appointments'}
              </Label>
              <Switch
                checked={newServiceForm.affects_general_appointments}
                disabled={!newServiceForm.allow_multi_booking}
                onCheckedChange={(checked) =>
                  setNewServiceForm((prev) => ({ ...prev, affects_general_appointments: checked }))
                }
              />
            </div>

            {newServiceForm.allow_multi_booking ? (
              <div className="space-y-3 rounded-[24px] bg-slate-50 p-4 dark:bg-slate-800">
                <div className="text-right">
                  <p className="text-base font-extrabold text-slate-900 dark:text-white">
                    {lang === 'ar' ? 'الخدمات المتأثرة' : 'Affected services'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                    {lang === 'ar'
                      ? 'اختر الخدمات التي تتأثر عند تفعيل الحجز المتعدد لهذه الخدمة'
                      : 'Choose the services affected when multi booking is enabled'}
                  </p>
                </div>

                {affectedServiceOptions.length ? (
                  <div className="space-y-3">
                    {affectedServiceOptions.map((service) => {
                      const checked = (newServiceForm.affected_service_ids || []).includes(service.id);
                      return (
                        <label
                          key={`affected-${service.localId}`}
                          className="flex cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
                        >
                          <div className="text-right">
                            <p className="font-bold text-slate-900 dark:text-white">
                              {service.name || (lang === 'ar' ? 'خدمة بدون اسم' : 'Untitled service')}
                            </p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                              {lang === 'ar' ? `${service.duration_minutes} دقيقة` : `${service.duration_minutes} min`}
                            </p>
                          </div>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) =>
                              setNewServiceForm((prev) => ({
                                ...prev,
                                affected_service_ids: value
                                  ? [...new Set([...(prev.affected_service_ids || []), service.id])]
                                  : (prev.affected_service_ids || []).filter((id) => id !== service.id),
                              }))
                            }
                          />
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-center text-sm font-medium text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                    {lang === 'ar' ? 'لا توجد خدمات أخرى متاحة للربط حالياً' : 'No other services available to link right now'}
                  </div>
                )}
              </div>
            ) : null}

            <div className="rounded-[24px] bg-slate-50 p-4 dark:bg-slate-800">
              <div className="flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => openCreateServiceScheduleDialog('__draft__')}
                  className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {lang === 'ar' ? 'إضافة' : 'Add'}
                </Button>
                <div className="text-right">
                  <p className="text-base font-extrabold text-slate-900 dark:text-white">
                    {lang === 'ar' ? 'الجداول' : 'Schedules'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                    {draftSchedules.length
                      ? lang === 'ar'
                        ? `${draftSchedules.length} جداول مضافة`
                        : `${draftSchedules.length} schedules added`
                      : lang === 'ar'
                        ? 'لا توجد جداول مضافة بعد'
                        : 'No schedules added yet'}
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {draftSchedules.length ? (
                  draftSchedules.map((serviceSchedule, scheduleIndex) => (
                    <div
                      key={serviceSchedule.localId || `draft-schedule-${scheduleIndex}`}
                      className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditServiceScheduleDialog('__draft__', scheduleIndex)}
                            className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteServiceSchedule('__draft__', scheduleIndex)}
                            className="text-red-500 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="min-w-[260px] text-right">
                          <div className="text-sm font-bold text-slate-900 dark:text-white">
                            {resolveDayLabel(serviceSchedule.day)}
                          </div>
                          <div className="mt-2 space-y-2">
                            <TimeEdgeBadge
                              label={lang === 'ar' ? 'من' : 'From'}
                              value={formatDisplayTime(serviceSchedule.start, lang)}
                              tone="start"
                            />
                            <TimeEdgeBadge
                              label={lang === 'ar' ? 'إلى' : 'To'}
                              value={formatDisplayTime(serviceSchedule.end, lang)}
                              tone="end"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-center text-sm font-medium text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                    {lang === 'ar' ? 'لا توجد جداول مضافة بعد' : 'No schedules added yet'}
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => handleCreateServiceDialogChange(false)}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="button" onClick={saveNewService} disabled={saving || loading} className="bg-teal-600 hover:bg-teal-700">
              {editingServiceLocalId
                ? lang === 'ar'
                  ? 'حفظ التعديلات'
                  : 'Save changes'
                : lang === 'ar'
                  ? 'إضافة الخدمة'
                  : 'Add service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorSchedule;
